/**
 * Compatibility shims for the staged migration. Each legacy `window.X`
 * function used by the existing modals/*.js files is mapped to its
 * equivalent in the new module tree.
 *
 * Two flavours of shim exist here:
 *   - **fillIn**: only sets `window.X` if not already defined. Used for
 *     utilities (escapeHtml etc.) that some legacy file already provides.
 *   - **takeOver**: unconditionally overrides `window.X` to point at the new
 *     module. Used when the new code OWNS the responsibility and the legacy
 *     file has been (or is being) deleted in this phase.
 */

import { escape } from '../shared/escape.js';
import { format as formatPlate, normalize as normalizePlate, badgeHtml } from '../shared/plate.js';
import { normalize as normalizePhone } from '../shared/phone.js';
import { haversineKm } from '../shared/geo.js';
import { debounce, throttle } from '../shared/timing.js';
import { withTimeout } from '../shared/promise.js';
import { waitForElById, runAfterFirstInteraction } from '../shared/dom.js';
import { reverseGeocode } from '../data/apis/locationiq.js';

import { auth, ref } from '../core/firebase.js';
import * as authService from '../features/auth/auth.service.js';
import * as deviceSvc from '../features/auth/device.service.js';
import * as loginView from '../features/auth/login.view.js';
import * as otpView from '../features/auth/otp.view.js';

import * as ownershipSvc from '../features/vehicle/ownership.service.js';
import * as licenseSvc from '../features/vehicle/license.service.js';
import * as vehicleSvc from '../features/vehicle/vehicle.service.js';
import * as myCarView from '../features/vehicle/myCar.view.js';
import * as licenseBannerView from '../features/vehicle/licenseBanner.view.js';
import * as vehicleDetailsView from '../features/vehicle/vehicleDetails.view.js';
import * as replaceVehicleView from '../features/vehicle/replaceVehicle.view.js';
import * as registerVehicleView from '../features/vehicle/register.view.js';
import * as searchGate from '../features/vehicle/searchGate.js';
import * as alertStatus from '../features/vehicle/alertStatus.js';
import * as plateScanner from '../features/vehicle/plateScanner.js';
import * as ckan from '../data/apis/ckan.js';
import { parseBaalutToISO, isoToYearMonth } from '../shared/date.js';

import * as smsSvc from '../features/messaging/sms.service.js';
import * as messageSvc from '../features/messaging/message.service.js';
import * as conversationSvc from '../features/messaging/conversation.service.js';
import * as pushSvc from '../features/notifications/push.service.js';
import * as admob from '../features/ads/admob.service.js';
import * as msgSendView from '../features/messaging/send.view.js';
import * as msgPresetView from '../features/messaging/preset.view.js';
import * as msgConversationView from '../features/messaging/conversation.view.js';
import * as msgInboxView from '../features/messaging/inbox.view.js';
import * as msgHistoryView from '../features/messaging/history.view.js';

import * as callSvc from '../features/calling/call.service.js';
import * as callView from '../features/calling/calling.view.js';
import * as dialTone from '../features/calling/dialingTone.js';
import * as callTracker from '../features/calling/callTracker.js';

import * as shareFeature from '../features/share/index.js';
import * as autoServicesFeature from '../features/autoServices/index.js';

import { showBlockedAccountModal } from '../shared/blockedAccountModal.js';

const w = /** @type {Record<string, unknown>} */ (/** @type {unknown} */ (window));

/** Attach `value` to `window[key]` only if not already defined. */
function fillIn(/** @type {string} */ key, /** @type {unknown} */ value) {
  if (typeof w[key] === 'undefined') w[key] = value;
}

/** Unconditionally attach `value` to `window[key]`. New module owns this. */
function takeOver(/** @type {string} */ key, /** @type {unknown} */ value) {
  w[key] = value;
}

// ============================================================================
// SHARED UTILITIES (fill-in only — legacy global-utils.js still defines these)
// ============================================================================
fillIn('escapeHtml', escape);
fillIn('_esc', escape);
fillIn('formatPlate', formatPlate);
fillIn('formatPlatePretty', formatPlate);
fillIn('normalizePlate', normalizePlate);
fillIn('plateBadgeHtml', (/** @type {unknown} */ p, /** @type {{small?: boolean}} */ opts) =>
  badgeHtml(p, opts ?? {}));
fillIn('normalizePhoneCanonical', normalizePhone);
fillIn('haversineKm', haversineKm);
fillIn('calculateDistance', haversineKm);
fillIn('debounce', debounce);
fillIn('throttle', throttle);
fillIn('withTimeout', withTimeout);
fillIn('waitForElById', waitForElById);
fillIn('runAfterFirstInteraction', runAfterFirstInteraction);

/**
 * `fetchWithTimeout(url, options)` — called by legacy `weather-card.js` and
 * `search-vehicle.js` but never defined anywhere in the legacy codebase
 * (was supposed to be in `weather-utils.js`). Provided here as a shim so the
 * legacy callers stop throwing ReferenceError.
 *
 * Honours `options.timeoutMs` (defaults to 8000) and forwards everything
 * else to the standard fetch.
 */
fillIn('fetchWithTimeout', (/** @type {string} */ url, /** @type {RequestInit & { timeoutMs?: number }} */ options = {}) => {
  const { timeoutMs = 8000, ...init } = options;
  return withTimeout(fetch(url, init), timeoutMs, `fetch ${url}`);
});
fillIn('getDetailedAddress', reverseGeocode);
fillIn('getDetailedAddressCached', reverseGeocode);

// ============================================================================
// AUTH (take-over — legacy auth-login.js / otp-simple.js / device-auth.js
// are being deleted in this phase)
// ============================================================================

/**
 * Legacy `registerOrLogin()` had no parameters — it read directly from the DOM.
 * The shim mirrors that contract so any old inline handler keeps working.
 */
takeOver('registerOrLogin', () => loginView.init() ?? login());

async function login() {
  const button = document.getElementById('authButton');
  if (button) button.click();
}

takeOver('validateLocalSession', () => authService.validateSession());
takeOver('logout', () => authService.logout('legacy-shim'));
takeOver('hardClientLogout', (/** @type {string} */ reason) => authService.hardLogout(reason || 'legacy-shim'));

/**
 * Designed blocked-account popup. Overrides the legacy `window.showBlockedPopup`
 * provided by `www/modals/ui-helpers.js` + `admin-modals.js` (both render the
 * old `#blockedPopup` drawer). Callers in `www/app.js` already pass
 * `(message, reason)` — the message string tells us whether it's a manual
 * block or a forced logout, and the reason is shown verbatim.
 */
takeOver('showBlockedPopup', async (/** @type {string} */ message, /** @type {string} */ reason) => {
  /** @type {'adminBlock' | 'adminKick'} */
  const context = /נותקת/.test(String(message || '')) ? 'adminKick' : 'adminBlock';
  await showBlockedAccountModal({ context, reason });
  // Match the legacy `closeBlockedPopup` behavior: after dismiss, route
  // back to a clean login state. The caller already signed-out + cleared
  // localStorage before invoking us, so a reload paints the login screen
  // without any stale chrome from the logged-in session.
  try { window.location.reload(); } catch (_) { /* ignore */ }
});
takeOver('showBlockedAccountModal', showBlockedAccountModal);
takeOver('closeBlockedPopup', () => {
  document.querySelectorAll('.blocked-overlay').forEach((n) => n.remove());
});

takeOver('getDeviceId', () => deviceSvc.getDeviceId());
takeOver('authorizeDeviceForPlate', (/** @type {string} */ plate) => deviceSvc.authorizeForPlate(plate));
takeOver('touchDeviceForPlate', (/** @type {string} */ plate) => deviceSvc.touchForPlate(plate));
takeOver('removeThisDeviceFromFirebase', (/** @type {string} */ plate) => deviceSvc.revokeForPlate(plate));

/** Legacy `startOtpFlow({phone,...})` used to send + show modal + commit. */
takeOver('startOtpFlow', (/** @type {{ phone: string }} */ opts) => otpView.runOtpFlow({ phone: opts?.phone || '' }));

/** Year dropdown — wired by legacy DOMContentLoaded. */
takeOver('populateProductionYearSelect', () => loginView.populateYearSelect());

/**
 * Persist a search to `users/{uid}/searchHistory/{plate}` so the account
 * drawer's history tab can display it. The legacy `search-vehicle.js`
 * calls `saveLastSearch(rec)` after each successful search; without this
 * shim the call falls through to the localStorage-only path and the
 * history tab stays empty.
 *
 * Schema (matches what account-drawer.js reads):
 *   users/{uid}/searchHistory/{plate}: {
 *     plate, phone?, lastSearchedAt, count
 *   }
 *
 * @param {{ plate?: string; phone?: string; model?: string; year?: string }} rec
 */
takeOver('saveLastSearch', async (rec) => {
  try {
    const plate = String(rec?.plate || '').replace(/\D/g, '').slice(0, 8);
    if (!plate) return;

    // Also keep the legacy localStorage record so feed cards that read it
    // still work. (Old fallback path also did this.)
    try {
      const ts = Date.now();
      localStorage.setItem('c2c_lastSearch', JSON.stringify({ ...rec, plate, ts }));
      const hist = JSON.parse(localStorage.getItem('c2c_searchHistory') || '[]');
      const newHist = [{ plate, ts }]
        .concat(hist.filter((/** @type {any} */ h) => h.plate !== plate))
        .slice(0, 10);
      localStorage.setItem('c2c_searchHistory', JSON.stringify(newHist));
    } catch { /* ignore */ }

    // Server-side history (read by account-drawer.js). Only writes if the
    // user is signed in — anonymous sessions still get the localStorage one.
    const uid = auth().currentUser?.uid;
    if (!uid) return;
    const r = ref(`users/${uid}/searchHistory/${plate}`);
    const existing = (await r.once('value')).val() || {};
    await r.update({
      plate,
      phone: rec?.phone ?? existing.phone ?? null,
      lastSearchedAt: Date.now(),
      count: (existing.count || 0) + 1,
    });
  } catch (err) {
    /* eslint-disable-next-line no-console */
    console.warn('[saveLastSearch] failed:', err);
  }
});

/**
 * Wipe the user's search-history (both Firebase and localStorage). Called
 * by the "🧹 נקה היסטוריה" button in the account drawer's history tab.
 * Was undefined before — the button confirmation fired but nothing happened.
 */
takeOver('clearSearchHistory', async () => {
  try {
    try {
      localStorage.removeItem('c2c_searchHistory');
      localStorage.removeItem('c2c_lastSearch');
    } catch { /* ignore */ }
    const uid = auth().currentUser?.uid;
    if (!uid) return;
    await ref(`users/${uid}/searchHistory`).remove();
  } catch (err) {
    /* eslint-disable-next-line no-console */
    console.warn('[clearSearchHistory] failed:', err);
  }
});

// ============================================================================
// VEHICLE (take-over — legacy my-car-render / vehicle-details / ownership-gov /
// license-banner / alert-status / ckan-api are being deleted in this phase)
// ============================================================================

// my-car-render.js
takeOver('renderMyCarInto', (/** @type {string} */ hostId) => myCarView.renderInto(hostId));
takeOver('renderMyCarSection', () => myCarView.renderSection());
takeOver('replaceVehicle', (/** @type {string} */ newPlate, /** @type {string} */ newYear) =>
  vehicleSvc.replace({ newPlate, year: newYear }));

// vehicle-details.js
takeOver('openVehicleDetailsModal', () => vehicleDetailsView.open());
takeOver('openReplaceModal', () => replaceVehicleView.open());
takeOver('closeReplaceModal', () => replaceVehicleView.close());

// Vehicle registration modal — UNIFIED with replace. `replaceVehicle.view`
// is now context-aware (detects register vs replace from LocalState.plate)
// so a single modal serves both flows. We keep `openRegisterVehicle` as an
// alias for any caller that may still rely on the name.
takeOver('openRegisterVehicle', () => replaceVehicleView.open());

/**
 * Gate the plate search for users without a registered vehicle. Returns
 * `true` if search may proceed; otherwise opens an explanation alert with
 * a CTA to the registration modal and returns `false`. Called from the
 * top of `onSearchVehiclesClick` in `www/modals/search-vehicle.js`.
 *
 * @returns {Promise<boolean>}
 */
takeOver('__requireVehicleOrPrompt', () => searchGate.requireVehicleOrPrompt());
takeOver('__hasRegisteredVehicle', () => searchGate.hasRegisteredVehicle());

// license-banner.js
takeOver('renderLicenseBannerAndExpiry', (/** @type {string} */ host) =>
  licenseBannerView.renderLicenseOnly(host || 'accountContent'));
takeOver('renderOwnershipBanner', (/** @type {string} */ host) =>
  licenseBannerView.renderOwnershipOnly(host || 'accountContent'));
takeOver('runMyCarBanners', (/** @type {string} */ host) =>
  licenseBannerView.render(host || 'accountContent'));
takeOver('readTokefFromDB', (/** @type {string} */ plate) => licenseSvc.readTokefFromDB(plate));
takeOver('parseTokefToDate', (/** @type {unknown} */ v) => licenseSvc.parseTokef(v));
takeOver('computeLicenseStatus', (/** @type {Date|null} */ d) => licenseSvc.computeStatus(d));
takeOver('fetchAndPersistGovBundle', (/** @type {string} */ plate) => ownershipSvc.ensureGovBundle(plate));

// alert-status.js
takeOver('checkAlertStatusAndUpdateDot', () => alertStatus.refresh());
takeOver('parseTokefDate', (/** @type {unknown} */ v) => licenseSvc.parseTokef(v));

// ownership-gov.js
takeOver('getLastOwnershipForPlate', (/** @type {string} */ plate) => ownershipSvc.getLastOwnership(plate));
takeOver('persistOwnershipDates', (/** @type {string} */ plate, /** @type {string} */ phone, /** @type {any} */ rec) =>
  ownershipSvc.persistOwnership(plate, phone, rec));
takeOver('ensureGovBundle', (/** @type {string} */ plate) => ownershipSvc.ensureGovBundle(plate));
takeOver('toISOFromBaalut', parseBaalutToISO);
takeOver('isoToYYYYMM', (/** @type {string} */ iso) => isoToYearMonth(iso)?.replace('-', ''));
takeOver('getStoredOwnershipDateISO', (/** @type {string} */ plate) => ownershipSvc.getStoredOwnershipISO(plate));

// ckan-api.js
takeOver('ckanSearch', (
  /** @type {string} */ resourceId,
  /** @type {{ filters?: Record<string, unknown>; q?: string; limit?: number }} */ opts,
) => ckan.search({ resourceId, filters: opts?.filters, q: opts?.q, limit: opts?.limit }));

/**
 * Legacy `checkDisabledTag(plate)` was called from `www/modals/search-vehicle.js`
 * but was NEVER defined anywhere — every search threw ReferenceError, the
 * silent catch defaulted to `false`, and the result card always read
 * "נכה: לא ❌" regardless of the actual tag status. This shim wires the
 * legacy global to the real data.gov.il lookup.
 *
 * @param {string} plate  any plate format (formatted or raw); we strip non-digits.
 * @returns {Promise<boolean>}
 */
takeOver('checkDisabledTag', async (/** @type {string} */ plate) => {
  const digits = String(plate || '').replace(/\D/g, '');
  if (!digits) return false;
  try {
    return await ckan.hasDisabledTag(digits);
  } catch (err) {
    /* eslint-disable-next-line no-console */
    console.warn('[checkDisabledTag] lookup failed:', err);
    return false;
  }
});
/**
 * Legacy `findVehicleAcrossDatasets` returned a shape the legacy
 * `search-vehicle.js` reads as `{ record, motStatus, extras }`. The new
 * `ckan.findVehicle` returns `{ records, resourceId }`. Without this
 * adapter the legacy renderer ends up with `record = undefined` and shows
 * "—" for make / model / colour even when the gov registry has data.
 */
takeOver('findVehicleAcrossDatasets', async (/** @type {string} */ plate) => {
  try {
    const result = await ckan.findVehicle(plate);
    const record = (result.records && result.records[0]) || null;
    /** @type {'ok' | 'empty' | 'down'} */
    const motStatus = record ? 'ok' : 'empty';

    // Best-effort ownership history fetch so the legacy `extras` shape is
    // populated too — used by date-of-ownership banners.
    /** @type {any} */
    let extras = null;
    try {
      const history = await ckan.fetchOwnershipHistory(plate);
      if (history && history.length > 0) {
        extras = { lastOwnership: history[0] };
      }
    } catch { /* ignore — ownership is best-effort */ }

    return { record, motStatus, extras };
  } catch (err) {
    return { record: null, motStatus: 'down', extras: null };
  }
});
takeOver('motLookupByPlate', async (/** @type {string} */ plate) => {
  const result = await ckan.findVehicle(plate);
  return result.records.length > 0
    ? { ok: true, record: result.records[0] }
    : { ok: false, notFound: true };
});

// ============================================================================
// MESSAGING (take-over — legacy files being deleted in this phase)
// ============================================================================

// send-message-modal.js
takeOver('openSendMessageModal', (/** @type {string} */ plate) => msgSendView.open(plate));
takeOver('closeSendMessageModal', () => msgSendView.close());

// chat-modal.js — the SMS-only quick modal is dropped; redirect to the unified
// send modal. Legacy callers passed `(plate, opts)` so we mirror the signature.
takeOver('openChatModal', (/** @type {string} */ plate) => msgSendView.open(plate));
takeOver('closeChatModal', () => msgSendView.close());

// preset-messages.js
takeOver('showPresetMessagesModal', (/** @type {string} */ plate) => msgPresetView.open(plate));
takeOver('openPresetPicker', (/** @type {string} */ plate) => msgPresetView.open(plate));
takeOver('closePresetPicker', () => msgPresetView.close());

// conversation-modal.js / conversation-send.js
takeOver('openConversationModal', (/** @type {string} */ peerPlate) => msgConversationView.open(peerPlate));
takeOver('openConversationDirectly', (/** @type {string} */ peerPlate) => msgConversationView.open(peerPlate));
takeOver('closeConversationModal', () => msgConversationView.close());
takeOver('sendMessageInConversation',
  (/** @type {string} */ toPlate, /** @type {string} */ body) =>
    messageSvc.send({ toPlate, body }),
);

// notifications-modal.js (inbox)
takeOver('showNotifications', () => msgInboxView.open());
takeOver('closeNotifications', () => msgInboxView.close());
takeOver('loadConversations', () => conversationSvc.list());
takeOver('loadConversationsDebounced', () => conversationSvc.list());
takeOver('markConversationAsRead', (/** @type {string} */ convId) =>
  messageSvc.markConversationRead(convId),
);

// message-history.js
takeOver('renderMessageHistorySectionInto',
  (/** @type {string} */ hostId) => msgHistoryView.renderInto(hostId),
);
takeOver('clearAllMessages', () => messageSvc.deleteAllSent());

// sms-chat.js / sms-utils.js
takeOver('sendChatMessageSmsV2',
  (/** @type {string} */ to, /** @type {string} */ body) => smsSvc.send({ to, body }),
);
takeOver('sendChatMessageSms',
  (/** @type {string} */ to, /** @type {string} */ body) => smsSvc.send({ to, body }),
);
takeOver('getTimeAgo', (/** @type {number} */ ms) => smsSvc.timeAgo(ms));
takeOver('sendSmsWithRetry', smsSvc.send);
takeOver('SMS_TIMEOUT_MS', 12_000);
takeOver('FIREBASE_TIMEOUT_MS', 8_000);

// fcm-notifications.js
takeOver('saveFCMToken', (/** @type {string} */ token) => pushSvc.saveToken(token));
takeOver('shouldProcessPush', (/** @type {any} */ p) => pushSvc.shouldProcess(p));
takeOver('loadUserNotifications', () => conversationSvc.list());

// ============================================================================
// CALLING (take-over — anonymous-calls.js + calling-screen.js + dialing-tone.js
// being deleted in this phase)
// ============================================================================

// anonymous-calls.js
takeOver('initiateAnonymousCall', (/** @type {string} */ plate) => callView.startWithConfirmation(plate));
takeOver('getCallHistory', () => callSvc.getHistory());

// calling-screen.js
takeOver('showCallingScreen', (/** @type {string} */ plate) => callView.start(plate));
takeOver('hideCallingScreen', () => callView.cancel());
takeOver('cancelCall', () => callView.cancel());
takeOver('confirmCall', (/** @type {string} */ plate) => callView.startWithConfirmation(plate));
takeOver('showCallConfirmation', (/** @type {string} */ plate) => callView.startWithConfirmation(plate));
takeOver('trackCallStatus',
  (/** @type {string} */ sid, /** @type {(s:any) => void} */ cb) =>
    callTracker.track(sid, cb ?? (() => {})),
);

// dialing-tone.js
takeOver('playDialingTonePulse', () => dialTone.start());
takeOver('stopDialingTonePulse', () => dialTone.stop());
takeOver('pauseDialingTonePulse', () => dialTone.stop());
takeOver('resumeDialingTonePulse', () => dialTone.start());
takeOver('isDialingTonePlaying', () => dialTone.isPlaying());

// ============================================================================
// SIDE FEATURES (share, auto-services)
// ============================================================================
takeOver('shareApp', () => shareFeature.shareApp());
takeOver('copyToClipboard', (/** @type {string} */ text) => shareFeature.copyToClipboard(text));
takeOver('openAutoServices', () => autoServicesFeature.open());
takeOver('closeAutoServices', () => autoServicesFeature.close());

// plate-scanner.js — exposes `window.PlateScanner` as an object with methods.
takeOver('PlateScanner', {
  open: () => plateScanner.open(),
  close: () => plateScanner.close(),
  openManualInput: () => plateScanner.openManualInput(),
  usePlate: (/** @type {string} */ plate) => plateScanner.usePlate(plate),
});

// ============================================================================
// ADMOB (take-over — legacy admob.js being replaced by features/ads/)
// ============================================================================
takeOver('initAdMob', () => admob.init());
takeOver('showBanner', () => admob.showBanner());
takeOver('hideBanner', () => admob.hideBanner());
takeOver('prepareInterstitial', () => Promise.resolve());      // pre-load happens in init
takeOver('showInterstitial', () => admob.showInterstitial());
takeOver('prepareRewardedAd', () => Promise.resolve());
takeOver('prepareRewardVideoAd', () => Promise.resolve());
takeOver('showRewardedAd', () => admob.showRewarded());
takeOver('getAdMobStatus', () => admob.status());
takeOver('showAdMobStatus', () => console.log(admob.status()));
/**
 * Legacy `trackAction(name)` — every N actions (modal opens, searches, etc.)
 * trigger a rewarded ad. The new admob.service handles its own rate-limit
 * (one rewarded per 2 min) so we just need to count actions and ask the
 * service to show one when the counter hits the threshold.
 */
const ACTIONS_BEFORE_AD = 4;
let actionCounter = 0;
takeOver('trackAction', async (/** @type {string} */ name) => {
  actionCounter += 1;
  if (actionCounter < ACTIONS_BEFORE_AD) {
    return { rewarded: false, skipped: true };
  }
  actionCounter = 0;
  try {
    return await admob.showRewarded();
  } catch {
    return { rewarded: false, skipped: true };
  }
});
takeOver('AdMobHelper', {
  init:               () => admob.init(),
  getStatus:          () => admob.status(),
  showBanner:         () => admob.showBanner(),
  hideBanner:         () => admob.hideBanner(),
  resumeBanner:       () => admob.showBanner(),
  prepareInterstitial: () => Promise.resolve(),
  showInterstitial:   () => admob.showInterstitial(),
  prepareRewardedAd:  () => Promise.resolve(),
  showRewardedAd:     () => admob.showRewarded(),
  isReady:            () => admob.status().initialised,
  isBannerVisible:    () => admob.status().banner.visible,
  isInterstitialLoaded: () => admob.status().interstitial.state === 'ready',
  isRewardedLoaded:   () => admob.status().rewarded.state === 'ready',
});
